import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { s } from './TissStyles';
import { getToday, getDateNDaysAgo } from './TissHelpers';
import { ToastProvider } from './TissShared';
import { useAuth } from '../../contexts/AuthContext';
import TabPainel from './TabPainel';
import TabGuias from './TabGuias';
import TabLotes from './TabLotes';
import TabConfig from './TabConfig';
import TabDiagnostico from './TabDiagnostico';

// Abas do dia a dia. Configuração e Diagnóstico ficam no menu "Avançado" (admin), fora do caminho.
const tabs = [
  { id: 'painel', label: 'Painel' },
  { id: 'guias', label: 'Guias' },
  { id: 'lotes', label: 'Lotes' },
];
const avancadas = [
  { id: 'config', label: 'Configuração' },
  { id: 'diagnostico', label: 'Diagnóstico' },
];

// Período ÚNICO da tela: vale para todas as abas, fica na URL (?de=&ate=) para sobreviver a
// recarregar/compartilhar e no localStorage para a próxima visita. Antes cada aba tinha seu
// próprio comportamento (Painel sem data, Lotes ignorando, Configuração sem receber o período).
const CHAVE_PERIODO = 'tiss-unimed-periodo';
function periodoInicial(searchParams) {
  const de = searchParams.get('de'); const ate = searchParams.get('ate');
  if (de !== null || ate !== null) return { de: de || '', ate: ate || '' };
  try {
    const salvo = JSON.parse(localStorage.getItem(CHAVE_PERIODO) || 'null');
    if (salvo && typeof salvo.de === 'string') return salvo;
  } catch { /* storage bloqueado: segue com o padrão */ }
  return { de: getDateNDaysAgo(30), ate: getToday() };
}

export default function TissUnimed() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const [periodo, setPeriodo] = useState(() => periodoInicial(searchParams));
  const abaUrl = searchParams.get('aba');
  const abasValidas = [...tabs, ...(isAdmin ? avancadas : [])].map(t => t.id);
  const activeTab = abasValidas.includes(abaUrl) ? abaUrl : 'painel';
  const [menuAvancado, setMenuAvancado] = useState(false);
  const [etapaInicial, setEtapaInicial] = useState(null);

  // Período e aba vão para a URL (replace: não poluir o histórico a cada digitação).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set('de', periodo.de); next.set('ate', periodo.ate);
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    try { localStorage.setItem(CHAVE_PERIODO, JSON.stringify(periodo)); } catch { /* ignora */ }
  }, [periodo]); // eslint-disable-line react-hooks/exhaustive-deps

  const irPara = useCallback((aba) => {
    const next = new URLSearchParams(searchParams);
    next.set('aba', aba);
    setSearchParams(next);
    setMenuAvancado(false);
  }, [searchParams, setSearchParams]);

  const handleNavigateGuias = (etapa) => { setEtapaInicial(etapa); irPara('guias'); };
  const handleTabChange = (id) => { if (id !== 'guias') setEtapaInicial(null); irPara(id); };

  const setDe = (v) => setPeriodo(p => ({ ...p, de: v }));
  const setAte = (v) => setPeriodo(p => ({ ...p, ate: v }));
  const atalho = (de, ate) => setPeriodo({ de, ate });
  const mesAtual = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
  const mesPassado = () => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
    const ini = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
    const fim = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return [ini, `${fim.getFullYear()}-${String(fim.getMonth() + 1).padStart(2, '0')}-${String(fim.getDate()).padStart(2, '0')}`];
  };
  const ehAvancada = avancadas.some(a => a.id === activeTab);
  const rotuloAvancada = avancadas.find(a => a.id === activeTab)?.label;

  return (
    <ToastProvider>
      <div style={s.page} className="data-page">
        <div style={s.breadcrumb}>
          <a href="/" style={s.breadcrumbLink}>Inicio</a> / <a href="/financeiro" style={s.breadcrumbLink}>Financeiro</a> / TISS UNIMED
        </div>
        {/* Migrado para o VIPLIS em 16/10/2026: o backend recusa toda escrita (000-somente-leitura.js). */}
        <div style={{ margin: '8px 0 12px', padding: '10px 14px', borderRadius: 6, background: '#fff4e5', border: '1px solid #f5c26b', color: '#5c3d00', fontSize: 14 }}>
          <b>Somente consulta.</b> O faturamento UNIMED foi migrado para o VIPLIS em 16/10/2026: pedidos de autorização,
          lotes e demonstrativos agora são feitos lá (Faturamento › Autorizações). Este histórico fica aqui para consulta.
        </div>
        <div style={s.pageHeader}>
          <div>
            <h1 style={s.pageTitle}>TISS UNIMED</h1>
            <p style={s.pageSubtitle}>Autorização, execução e faturamento de guias SADT</p>
          </div>
        </div>

        {/* Abas + menu Avançado */}
        <div style={{ ...s.tabs, alignItems: 'center' }}>
          {tabs.map(t => (
            <button key={t.id}
              style={{ ...s.tab, ...(activeTab === t.id ? s.tabActive : {}) }}
              onClick={() => handleTabChange(t.id)}>
              {t.label}
            </button>
          ))}
          {ehAvancada && (
            <button style={{ ...s.tab, ...s.tabActive }}>{rotuloAvancada}</button>
          )}
          {isAdmin && (
            <div style={{ marginLeft: 'auto', position: 'relative' }}>
              <button style={{ ...s.btnSmall, color: '#666' }} onClick={() => setMenuAvancado(v => !v)}
                title="Configuração da integração e diagnóstico das transações">⚙ Avançado ▾</button>
              {menuAvancado && (
                <div style={{ position: 'absolute', right: 0, top: '30px', background: '#fff', border: '1px solid #ddd',
                  borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 20, minWidth: '170px' }}>
                  {avancadas.map(a => (
                    <button key={a.id} onClick={() => irPara(a.id)}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '9px 14px', border: 'none',
                        background: activeTab === a.id ? '#f0f4f8' : 'transparent', cursor: 'pointer', fontSize: '13px' }}>
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Período — o mesmo para todas as abas */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 14px',
          backgroundColor: '#f8f9fa', border: '1px solid #e6e8eb', borderRadius: '6px',
          marginBottom: '14px', flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '12px', color: '#555', fontWeight: '600' }}>Período</span>
          <input type="date" style={{ ...s.input, width: '140px' }} value={periodo.de} onChange={e => setDe(e.target.value)} />
          <span style={{ fontSize: '12px', color: '#999' }}>até</span>
          <input type="date" style={{ ...s.input, width: '140px' }} value={periodo.ate} onChange={e => setAte(e.target.value)} />
          <div style={{ display: 'flex', gap: '4px' }}>
            <button style={s.btnSmall} onClick={() => atalho(getToday(), getToday())}>Hoje</button>
            <button style={s.btnSmall} onClick={() => atalho(getDateNDaysAgo(7), getToday())}>7 dias</button>
            <button style={s.btnSmall} onClick={() => atalho(getDateNDaysAgo(30), getToday())}>30 dias</button>
            <button style={s.btnSmall} onClick={() => atalho(mesAtual(), getToday())}>Este mês</button>
            <button style={s.btnSmall} onClick={() => atalho(...mesPassado())}>Mês passado</button>
            <button style={s.btnSmall} onClick={() => atalho('', '')}>Tudo</button>
          </div>
        </div>

        {activeTab === 'painel' && (
          <TabPainel onNavigateGuias={handleNavigateGuias} dataInicio={periodo.de} dataFim={periodo.ate} />
        )}
        {activeTab === 'guias' && (
          <TabGuias dataInicio={periodo.de} dataFim={periodo.ate} etapaInicial={etapaInicial} />
        )}
        {activeTab === 'lotes' && <TabLotes dataInicio={periodo.de} dataFim={periodo.ate} />}
        {activeTab === 'config' && isAdmin && <TabConfig dataInicio={periodo.de} dataFim={periodo.ate} />}
        {activeTab === 'diagnostico' && isAdmin && <TabDiagnostico dataInicio={periodo.de} dataFim={periodo.ate} />}
      </div>
    </ToastProvider>
  );
}
